/**
 * The "All fields" board's multi-code rows (BV-2742).
 *
 * With two or more codes, an entity's fields would otherwise repeat once per code
 * (J1234 → Coverage Status, Copay, …; J1235 → the same again). Here each field is ONE row
 * with a chip per code, matching the Call Script's grid:
 *   - filled (captured or prefilled) → primary outline
 *   - required and still empty → critical outline, same as the board's "Missing" dot
 *   - heard → attention outline
 *   - anything else → greyed out
 * Chips only navigate: a click reports the code's field key, and the Call Script scrolls
 * to that cell. Values are edited in the grid.
 */
import React from 'react';

import { cn } from '@/utils';
import type { BoardCodeRow, BoardField } from '@/utils/humanCallModel';

/** Format a field value, resolving enum codes to human labels via field.options. */
export function formatFieldValue(
  value: unknown,
  field: { options?: { value: unknown; label: string }[] },
): string {
  if (value === undefined || value === null || value === '') return '—';
  const label = (v: unknown) =>
    field.options?.find(o => String(o.value) === String(v))?.label ?? String(v);
  return Array.isArray(value) ? value.map(label).join(', ') : label(value);
}

type ChipTone = 'filled' | 'missing' | 'heard' | 'empty';

const CHIP_CLS: Record<ChipTone, string> = {
  filled: 'border-primary text-foreground',
  missing: 'border-critical text-critical-foreground',
  heard: 'border-attention text-attention-foreground',
  empty: 'border-border text-muted-foreground',
};

function chipTone(f: BoardField): ChipTone {
  if (f.status === 'captured' || f.status === 'prefilled') return 'filled';
  if (f.status === 'missing' && f.required) return 'missing';
  if (f.status === 'heard') return 'heard';
  return 'empty';
}

/** What the chip's tooltip and accessible name say about the code's value. */
function chipState(f: BoardField, tone: ChipTone): string {
  if (tone === 'filled') return formatFieldValue(f.value, f.field);
  if (tone === 'missing') return 'missing';
  if (tone === 'heard') return 'heard';
  return 'empty';
}

interface BoardCodeRowsProps {
  sections: { section: string | undefined; rows: BoardCodeRow[] }[];
  /** The chip last clicked or revealed; its row is highlighted. */
  selectedKey: string | null;
  fieldErrors?: Record<string, string>;
  onCellClick: (key: string) => void;
}

/**
 * The rows in section order, WITHOUT the nested-object sub-headers ("Coverage", "Prior
 * Auth Status") the per-code blocks show: the board section already names the topic, and
 * the chips are what the rep scans.
 */
export function BoardCodeRows({ sections, ...rowProps }: BoardCodeRowsProps) {
  return (
    <>
      {sections.flatMap(sub =>
        sub.rows.map(row => <CodeRow key={row.rowKey} row={row} {...rowProps} />),
      )}
    </>
  );
}

function CodeRow({
  row,
  selectedKey,
  fieldErrors,
  onCellClick,
}: Omit<BoardCodeRowsProps, 'sections'> & { row: BoardCodeRow }) {
  const errors = row.cells.filter(c => fieldErrors?.[c.field.key]);
  const selected = row.cells.some(c => c.field.key === selectedKey);

  return (
    <div className={cn('rounded-md px-2 py-1.5', selected && 'bg-primary/10')}>
      <div className="break-words text-[12.5px] text-foreground">{row.label}</div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {row.cells.map(({ instance, field }) => {
          const tone = chipTone(field);
          const state = chipState(field, tone);
          return (
            <button
              key={field.key}
              type="button"
              data-field-id={field.key}
              aria-label={`${row.label}, ${instance.label}: ${state}`}
              title={`${instance.label}: ${state}`}
              onClick={() => onCellClick(field.key)}
              className={cn(
                'rounded-full border px-2 py-0.5 text-[11.5px] font-medium leading-4 hover:bg-muted/60',
                CHIP_CLS[tone],
                fieldErrors?.[field.key] && 'border-destructive text-destructive',
                field.key === selectedKey && 'ring-2 ring-primary/40',
              )}
            >
              {instance.label}
            </button>
          );
        })}
      </div>

      {errors.map(({ instance, field }) => (
        <p key={field.key} className="pt-1 text-[11px] text-destructive">
          {instance.label}: {fieldErrors?.[field.key]}
        </p>
      ))}
    </div>
  );
}
